/* File: frontend/src/components/reputation/AgentBattleInterface.js */
/* Component: Agent Battle Interface (Visualizing Truth & Operation Phoenix) */
/* Version: 6.0 (Relational History + Auto-Task Visualization) */
/* Protocol v3.0: UI Stability & Data Integrity Checks Active */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Send, Loader2, Sparkles,
    Lock, Check, LayoutGrid, History, X, PlusCircle,
    StopCircle, Play, Zap,
    Search, Bot, Target,
    Scale, Crown, Crosshair, ShieldAlert, Feather, Code, Link, Briefcase, Activity,
    AlertTriangle, FileText, ArrowRight, Flame, // New Icons for Operation Phoenix
    Brain, Cloud, Globe, ChevronLeft, ChevronRight // Battle Workers v2: Claude/Copilot/Perplexity + scroll indicators
} from 'lucide-react';

// --- INTERNAL CONFIG (Safe Fallback) ---
const WORKERS = [
    { id: 'w_judge', name: "Hon. Arthur", role: "The Judge", icon: Scale, color: "#64748b", locked: true, defaultSelected: true, tools: ["Logic", "Rules"] },
    { id: 'w_hive', name: "Ava (Architect)", role: "Hive Orchestrator", icon: Crown, color: "#eab308", locked: false, defaultSelected: false, tools: ["Context", "Flow"] },
    { id: 'w_seo', name: "Elena", role: "SEO Sniper", icon: Crosshair, color: "#ef4444", locked: false, defaultSelected: false, tools: ["Keywords", "SERP"] },
    { id: 'w_pr', name: "Marcus", role: "Crisis PR Lead", icon: ShieldAlert, color: "#3b82f6", locked: false, defaultSelected: false, tools: ["Spin", "Press"] },
    { id: 'w_copy', name: "Sarah", role: "Lead Storyteller", icon: Feather, color: "#f97316", locked: false, defaultSelected: false, tools: ["Narrative", "Tone"] },
    { id: 'w_code', name: "David (Tech)", role: "Technical Lead", icon: Code, color: "#10b981", locked: false, defaultSelected: false, tools: ["Code", "Security"] },
    { id: 'w_ux', name: "Dr. Olivia", role: "Behavioral Profiler", icon: Activity, color: "#8b5cf6", locked: false, defaultSelected: false, tools: ["UX", "Psych"] },
    { id: 'w_strategy', name: "Leo", role: "Strategic Disruptor", icon: Zap, color: "#f59e0b", locked: false, defaultSelected: false, tools: ["Pivot", "Growth"] },
    { id: 'w_research', name: "Margaret", role: "Deep Code", icon: Link, color: "#06b6d4", locked: false, defaultSelected: false, tools: ["Data", "Source"] },
    { id: 'w_closer', name: "James", role: "The Closer", icon: Briefcase, color: "#84cc16", locked: false, defaultSelected: false, tools: ["Sales", "Deal"] },
];

// --- 1. SLICER LOGIC ---
const parseStreams = (messages) => {
    const streams = {
        all: [], google: [], gpt4: [], gemini: [], deepseek: [], grok: [],
        claude: [], copilot: [], perplexity: [],
    };
    if (!messages) return streams;

    messages.forEach(msg => {
        if (msg.role === 'user') {
            Object.keys(streams).forEach(key => streams[key].push(msg));
        } else {
            streams.all.push(msg);
            const content = msg.content || "";
            const parts = content.split(/(?=###\s+)/g);
            parts.forEach(part => {
                const lower = part.toLowerCase();
                // Order matters — more specific labels first (e.g. 'gpt4' before plain 'gpt')
                if (lower.includes('perplexity')) streams.perplexity.push({ ...msg, content: part });
                else if (lower.includes('copilot') || lower.includes('azure')) streams.copilot.push({ ...msg, content: part });
                else if (lower.includes('claude') || lower.includes('anthropic')) streams.claude.push({ ...msg, content: part });
                else if (lower.includes('google')) streams.google.push({ ...msg, content: part });
                else if (lower.includes('gpt')) streams.gpt4.push({ ...msg, content: part });
                else if (lower.includes('gemini')) streams.gemini.push({ ...msg, content: part });
                else if (lower.includes('deepseek')) streams.deepseek.push({ ...msg, content: part });
                else if (lower.includes('grok')) streams.grok.push({ ...msg, content: part });
            });
        }
    });
    return streams;
};

// --- WIN/LOSS ANALYZER (Multi-Tenant Aware) ---
const analyzeWinLoss = (text) => {
    if (!text) return null;
    const lower = text.toLowerCase();
    if (/1\.\s*\*?(fitenko|ekaterina|morgan)/i.test(text)) return 'WIN';
    if (lower.includes('fitenko') || lower.includes('morgan legal') || lower.includes('clear winner')) return 'RANKED';
    return 'LOSS';
};

const AgentBattleInterface = ({
    sessions = [], activeSessionId, setActiveSessionId,
    messages = [], isTyping, inputMsg, setInputMsg,
    sendReply, startNewBattle, onClose,
    isFullScreen = false,
    isAutoMode, toggleAutoMode,
    // [FIX] Persistence Props
    savedSquad = [], onSquadChange
}) => {
    const messagesEndRef = useRef(null);
    const [selectedWorkers, setSelectedWorkers] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [turnCount, setTurnCount] = useState(0);

    // --- INIT ---
    useEffect(() => {
        if (savedSquad && savedSquad.length > 0) {
            setSelectedWorkers(savedSquad);
        } else {
            const defaults = WORKERS.filter(w => w.defaultSelected).map(w => w.id);
            setSelectedWorkers(defaults);
        }
    }, [savedSquad]);

    // --- COMPUTED DATA ---
    const streams = useMemo(() => parseStreams(messages), [messages]);
    const currentMessages = streams[activeTab] || [];

    // Find active session securely
    const activeSession = useMemo(() =>
        sessions.find(s => s.id === activeSessionId || s.session_id === activeSessionId),
        [sessions, activeSessionId]);

    // --- SCROLL LOGIC ---
    const scrollContainerRef = useRef(null);
    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

    const handleScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const { scrollTop, scrollHeight, clientHeight } = container;
        // User is "near bottom" if within 100px
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShouldAutoScroll(isNearBottom);
    };

    // 1. Force scroll on session/tab switch
    useEffect(() => {
        if (activeSessionId) {
            setShouldAutoScroll(true);
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [activeSessionId, activeTab]);

    // 2. Conditional scroll on new messages/typing
    useEffect(() => {
        if (activeSessionId && shouldAutoScroll) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, isTyping]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendReply();
        }
    };

    // --- WORKER TOGGLE ---
    const toggleWorker = (workerId) => {
        // If user interacts with Squad Panel while viewing history, switch to "New Battle" mode
        if (activeSessionId) setActiveSessionId(null);

        const worker = WORKERS.find(w => w.id === workerId);
        if (worker.locked) return;

        setSelectedWorkers(prev => {
            const next = prev.includes(workerId) ? prev.filter(id => id !== workerId) : [...prev, workerId];
            if (onSquadChange) onSquadChange(next); // [FIX] Sync to parent
            return next;
        });
    };

    // --- START BATTLE ---
    const handleStartBattle = () => {
        if (selectedWorkers.length === 0) return alert("The Judge must be active.");

        // Ensure we are in "New Battle" mode
        if (activeSessionId) setActiveSessionId(null);

        const selectedIds = selectedWorkers;
        // Battle Workers v2 — 7 engine slots. The backend run_quad_core_battle
        // honors profile.active_workers, so passing all-true here means each
        // profile's own toggles in Source of Truth decide which engines fire.
        const aiModels = ['gpt4', 'gemini', 'deepseek_r1', 'grok', 'claude', 'copilot', 'perplexity'];
        startNewBattle({
            worker_count: selectedIds.length + aiModels.length,
            worker_keys: [...aiModels, ...selectedIds],
            squad_ids: selectedIds,
            active_workers: {
                gpt: true, gemini: true, deepseek: true, grok: true,
                claude: true, copilot: true, perplexity: true,
            },
        });
    };

    // --- AUTO LOGIC ---
    useEffect(() => {
        if (isAutoMode && !isTyping && messages.length > 0) {
            const lastMsg = messages[messages.length - 1];
            if (lastMsg?.role === 'assistant') {
                // Stop if we see mock echoed content or if Protocol Phoenix has already engaged
                if (lastMsg.content.includes("Received:") || lastMsg.content.includes("PROTOCOL PHOENIX ENGAGED")) {
                    toggleAutoMode();
                    return;
                }

                // Reduce Spam: Limit retries to 2
                if (turnCount >= 2) { toggleAutoMode(); return; }

                const content = lastMsg.content || "";
                if (analyzeWinLoss(content) === 'WIN') {
                    toggleAutoMode();
                } else {
                    setTimeout(() => {
                        if (isAutoMode) {
                            sendReply("Analyze failure. Execute Protocol Phoenix.");
                            setTurnCount(p => p + 1);
                        }
                    }, 4000);
                }
            }
        }
    }, [messages, isAutoMode, isTyping]);

    // Engine tabs — color-coded for fast scanning. Each engine has a brand-ish
    // tint applied to the active-state underline + dot. Order: aggregate first,
    // then incumbents (Google/GPT/Gemini), then premium (Claude/Copilot), then
    // alternative (Perplexity/Grok/DeepSeek).
    const TABS = [
        { id: 'all',        label: 'All',        icon: LayoutGrid, color: '#3b82f6' },
        { id: 'google',     label: 'Google',     icon: Search,     color: '#4285F4' },
        { id: 'gpt4',       label: 'GPT-4',      icon: Bot,        color: '#10a37f' },
        { id: 'gemini',     label: 'Gemini',     icon: Zap,        color: '#8E75B2' },
        { id: 'claude',     label: 'Claude',     icon: Brain,      color: '#D97757' },
        { id: 'copilot',    label: 'Copilot',    icon: Cloud,      color: '#0078D4' },
        { id: 'perplexity', label: 'Perplexity', icon: Globe,      color: '#20A39E' },
        { id: 'deepseek',   label: 'DeepSeek',   icon: Target,     color: '#4D6BFE' },
        { id: 'grok',       label: 'Grok',       icon: Sparkles,   color: '#000000' },
    ];

    // Tabs container — horizontally scrollable when overflowing
    const tabsScrollRef = useRef(null);
    const scrollTabs = (delta) => {
        if (tabsScrollRef.current) {
            tabsScrollRef.current.scrollBy({ left: delta, behavior: 'smooth' });
        }
    };

    return (
        <div className={`chat-layout-wrapper ${isFullScreen ? 'fullscreen-mode' : ''}`} style={{ display: 'flex', height: '75vh', background: '#FCFBF8', borderRadius: '12px', border: '1px solid #E5E1D6', overflow: 'hidden' }}>
            {/* Hide webkit scrollbar for tabs strip */}
            <style>{`.mlg-tabs-scroll::-webkit-scrollbar { display: none; }`}</style>

            {/* --- LEFT SIDEBAR: MISSION LOG --- */}
            <div className="chat-sidebar" style={{ width: '220px', background: '#fff', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '16px', borderBottom: '1px solid #F1EEE4', fontWeight: '700', fontSize: '0.75rem', color: '#64748b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#FCFBF8', height: '56px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <LayoutGrid size={14} /> MISSION LOG
                    </span>
                    <button onClick={() => setActiveSessionId(null)} style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
                        padding: '4px 10px', fontSize: '0.7rem', fontWeight: '600',
                        color: '#3b82f6', cursor: 'pointer', transition: 'all 0.2s'
                    }}>
                        <PlusCircle size={12} /> New Battle
                    </button>
                </div>
                <div className="custom-scroll" style={{ flex: 1, overflowY: 'auto' }}>
                    {sessions.map(s => {
                        const isActive = activeSessionId === (s.id || s.session_id);
                        const results = s.results || [];
                        return (
                            <div
                                key={s.id || s.session_id}
                                onClick={() => setActiveSessionId(s.id || s.session_id)}
                                style={{
                                    padding: '12px 16px', borderBottom: '1px solid #f8fafc', cursor: 'pointer',
                                    background: isActive ? '#eff6ff' : 'transparent',
                                    borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#0f172a', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {(s.title || 'Mission').replace('Auto:', '').trim()}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {results.length > 0 ? results.map((r, i) => (
                                        <div key={i} title={`${r.engine}: ${r.status}`} style={{
                                            width: 6, height: 6, borderRadius: '50%',
                                            backgroundColor: r.status === 'WIN' ? '#10b981' : (r.status === 'LOSS' ? '#ef4444' : '#f59e0b')
                                        }} />
                                    )) : <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Pending...</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* --- CENTER: ARENA --- */}
            <div className="chat-main-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#FCFBF8', borderRight: '1px solid #e2e8f0' }}>

                {/* HEADER: Arena title + Controls (row 1) + Engine Tabs (row 2) */}
                <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
                    {/* Row 1 — Title + battle controls */}
                    <div style={{ padding: '0 20px', height: '56px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>Arena</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <button
                                onClick={handleStartBattle}
                                disabled={isTyping}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    background: '#fff', border: '1px solid #E5E1D6', color: '#334155',
                                    padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600',
                                    cursor: isTyping ? 'not-allowed' : 'pointer',
                                    opacity: isTyping ? 0.6 : 1
                                }}
                            >
                                <Sparkles size={14} /> Interrogate
                            </button>

                            <select style={{ background: '#fff', border: '1px solid #E5E1D6', padding: '6px', borderRadius: '6px', fontSize: '0.75rem', outline: 'none', color: '#475569' }} defaultValue="5m">
                                <option value="1m">1m (Debug)</option>
                                <option value="5m">5 min</option>
                                <option value="15m">15 min</option>
                                <option value="1h">1h</option>
                                <option value="24h">24h</option>
                            </select>

                            <button onClick={toggleAutoMode} style={{
                                border: '1px solid #3b82f6', cursor: 'pointer',
                                background: isAutoMode ? '#eff6ff' : '#fff', color: '#3b82f6',
                                padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px'
                            }}>
                                {isAutoMode ? <StopCircle size={14} /> : <Play size={14} />}
                                AUTO
                            </button>
                        </div>
                    </div>

                    {/* Row 2 — Engine Tabs (scrollable, full width) */}
                    {activeSessionId && (
                        <div style={{
                            padding: '6px 12px 8px',
                            borderTop: '1px solid #f1f5f9',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            background: '#fafbfc',
                        }}>
                            <button
                                onClick={() => scrollTabs(-180)}
                                title="Scroll left"
                                style={{
                                    border: '1px solid #E5E1D6', background: '#fff',
                                    color: '#64748b', cursor: 'pointer',
                                    padding: '5px 7px', borderRadius: '6px',
                                    display: 'flex', alignItems: 'center', flexShrink: 0,
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#1e293b'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; }}
                            >
                                <ChevronLeft size={14} />
                            </button>

                            <div
                                ref={tabsScrollRef}
                                className="mlg-tabs-scroll"
                                style={{
                                    display: 'flex', gap: '4px',
                                    overflowX: 'auto', overflowY: 'hidden',
                                    flex: 1, minWidth: 0,
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                    scrollBehavior: 'smooth',
                                }}
                            >
                                {TABS.map(tab => {
                                    const isActive = activeTab === tab.id;
                                    const baseColor = tab.color || '#3b82f6';
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            style={{
                                                border: isActive ? `1px solid ${baseColor}40` : '1px solid transparent',
                                                cursor: 'pointer',
                                                background: isActive ? `${baseColor}15` : 'transparent',
                                                color: isActive ? baseColor : '#64748b',
                                                fontSize: '0.75rem',
                                                fontWeight: isActive ? '700' : '600',
                                                padding: '6px 12px',
                                                borderRadius: '8px',
                                                display: 'flex', alignItems: 'center', gap: '6px',
                                                whiteSpace: 'nowrap', flexShrink: 0,
                                                transition: 'all 0.2s ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isActive) {
                                                    e.currentTarget.style.background = `${baseColor}0d`;
                                                    e.currentTarget.style.color = baseColor;
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isActive) {
                                                    e.currentTarget.style.background = 'transparent';
                                                    e.currentTarget.style.color = '#64748b';
                                                }
                                            }}
                                        >
                                            <tab.icon size={13} />
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => scrollTabs(180)}
                                title="Scroll right"
                                style={{
                                    border: '1px solid #E5E1D6', background: '#fff',
                                    color: '#64748b', cursor: 'pointer',
                                    padding: '5px 7px', borderRadius: '6px',
                                    display: 'flex', alignItems: 'center', flexShrink: 0,
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#1e293b'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; }}
                            >
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    )}
                </div>

                {/* MESSAGES STREAM */}
                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className="custom-scroll"
                    style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}
                >

                    {/* --- OPERATION PHOENIX ALERT (If Triggered) --- */}
                    {activeSession?.triggered_tasks && activeSession.triggered_tasks.length > 0 && (
                        <div style={{
                            backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px',
                            display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '10px'
                        }}>
                            <div style={{ padding: '6px', backgroundColor: '#fee2e2', borderRadius: '6px', color: '#ef4444' }}>
                                <AlertTriangle size={18} />
                            </div>
                            <div>
                                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#991b1b', marginBottom: '4px' }}>
                                    OPERATION PHOENIX ACTIVATED
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#b91c1c', lineHeight: '1.4' }}>
                                    <strong>Trigger:</strong> Organic Audit Failure (Loss)<br />
                                    <strong>Action:</strong> {activeSession.triggered_tasks[0].type} (Hyper-Local Cornerstone)<br />
                                    <strong>Status:</strong> <span style={{ backgroundColor: '#fff', padding: '2px 4px', borderRadius: '4px', border: '1px solid #fecaca' }}>QUEUED IN CONTENT WARFARE</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {!activeSessionId && !isTyping && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                            <div style={{ marginBottom: '16px', color: '#cbd5e1' }}>
                                <LayoutGrid size={48} strokeWidth={1} />
                            </div>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Ready for Deployment</h3>
                            <p style={{ fontSize: '0.9rem', maxWidth: '300px', margin: '0 auto', lineHeight: '1.5' }}>
                                Select your <strong>Active Squad</strong> on the right, then click <span style={{ color: '#C8A653', fontWeight: '600' }}>Interrogate</span> to begin a new battle session.
                            </p>
                        </div>
                    )}

                    {/* Empty state when an engine has no data yet */}
                    {activeSessionId && activeTab !== 'all' && currentMessages.filter(m => m.role !== 'user').length === 0 && (
                        <div style={{
                            margin: '20px auto',
                            maxWidth: '420px',
                            background: '#fff',
                            border: '1px dashed #cbd5e1',
                            borderRadius: '10px',
                            padding: '24px 20px',
                            textAlign: 'center',
                            color: '#64748b',
                        }}>
                            {(() => {
                                const tab = TABS.find(t => t.id === activeTab);
                                const Icon = tab?.icon || LayoutGrid;
                                const tint = tab?.color || '#94a3b8';
                                return (
                                    <>
                                        <div style={{
                                            display: 'inline-flex', padding: '10px',
                                            background: `${tint}14`, borderRadius: '50%',
                                            color: tint, marginBottom: '10px',
                                        }}>
                                            <Icon size={20} />
                                        </div>
                                        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                                            No {tab?.label || activeTab} data in this battle yet
                                        </div>
                                        <div style={{ fontSize: '0.75rem', lineHeight: '1.5' }}>
                                            Either this engine wasn't enabled in <strong>Source of Truth → Battle Workers</strong>,
                                            or the worker hasn't responded yet. Switch to <strong>All</strong> for a combined view.
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {activeSessionId && currentMessages.map((msg, idx) => {
                        const status = (msg.role === 'assistant' && activeTab !== 'all') ? analyzeWinLoss(msg.content) : null;
                        const isUser = msg.role === 'user';
                        return (
                            <div key={idx} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', gap: '10px' }}>
                                {!isUser && <div style={{ width: 28, height: 28, borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Bot size={16} color="#64748b" /></div>}
                                <div style={{
                                    maxWidth: '80%', padding: '12px 16px', borderRadius: '8px', fontSize: '0.8rem', lineHeight: '1.5', whiteSpace: 'pre-wrap', position: 'relative',
                                    background: isUser ? '#eff6ff' : (status === 'WIN' ? '#f0fdf4' : (status === 'LOSS' ? '#fef2f2' : '#fff')),
                                    border: isUser ? 'none' : (status === 'WIN' ? '1px solid #10b981' : (status === 'LOSS' ? '1px solid #ef4444' : '1px solid #e2e8f0')),
                                    color: isUser ? '#1e3a8a' : '#0f172a',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}>
                                    {status && <div style={{
                                        position: 'absolute', top: -8, right: 10, fontSize: '0.6rem', fontWeight: '800',
                                        padding: '2px 6px', borderRadius: '4px', color: '#fff',
                                        background: status === 'WIN' ? '#10b981' : (status === 'RANKED' ? '#f59e0b' : '#ef4444')
                                    }}>{status}</div>}
                                    {msg.content}
                                </div>
                            </div>
                        );
                    })}
                    {isTyping && (
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ width: 28, height: 28, borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Bot size={16} color="#64748b" /></div>
                            <div style={{ padding: '12px', background: '#FCFBF8', borderRadius: '8px', fontSize: '0.8rem', fontStyle: 'italic', color: '#64748b' }}>
                                <Loader2 size={14} className="spin inline mr-2" /> Evaluating rankings...
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* INPUT ZONE */}
                {activeSessionId && (
                    <div style={{ padding: '12px 16px', background: '#fff', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                                onClick={() => sendReply("Analyze failure. Execute Protocol Phoenix.")}
                                disabled={isTyping}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    padding: '5px 10px', borderRadius: '6px',
                                    background: '#fff1f2', border: '1px solid #fecdd3', color: '#e11d48',
                                    fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer',
                                    transition: 'all 0.15s'
                                }}
                                title="Autonomous Counter-Attack: Analyze competitor ranking and create Strategic Task"
                            >
                                <Flame size={13} color="#e11d48" /> Ignite Protocol Phoenix
                            </button>
                            <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Auto-heals AI ranking deficits</span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <input
                                style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', border: '1px solid #E5E1D6', fontSize: '0.85rem', outline: 'none', background: '#FCFBF8' }}
                                placeholder={isAutoMode ? "Auto-Pilot active..." : "Enter command (e.g. 'Analyze failure. Execute Protocol Phoenix.')..."}
                                value={inputMsg} onChange={(e) => setInputMsg(e.target.value)} onKeyDown={handleKeyDown} disabled={isTyping || isAutoMode}
                            />
                            <button onClick={() => sendReply()} disabled={!inputMsg.trim() || isTyping || isAutoMode}
                                style={{ background: '#20231F', color: '#FFFFFF', border: 'none', width: '40px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Send size={18} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* --- RIGHT SIDEBAR: SQUAD PANEL (Platinum Layout) --- */}
            <div className="chat-right-sidebar" style={{ width: '300px', background: '#fff', borderLeft: '1px solid #E5E1D6', display: 'flex', flexDirection: 'column' }}>

                {/* Header */}
                <div style={{ padding: '16px', borderBottom: '1px solid #F1EEE4', height: '56px', display: 'flex', alignItems: 'center', background: '#FCFBF8' }}>
                    <h3 style={{ margin: 0, fontSize: '0.75rem', fontWeight: '700', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        <ShieldAlert size={14} /> ACTIVE SQUAD ({selectedWorkers.length})
                    </h3>
                </div>

                {/* Workers List */}
                <div className="custom-scroll" style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {WORKERS.map(worker => {
                            const isSelected = selectedWorkers.includes(worker.id);
                            const IconComponent = worker.icon;
                            return (
                                <div key={worker.id} onClick={() => toggleWorker(worker.id)}
                                    style={{
                                        border: isSelected ? `1px solid ${worker.color}` : '1px solid #f1f5f9',
                                        background: isSelected ? '#fff' : '#fff',
                                        borderRadius: '8px', padding: '10px',
                                        cursor: worker.locked ? 'default' : 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        boxShadow: isSelected ? `0 0 0 1px ${worker.color}20` : 'none',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{
                                            width: 32, height: 32, borderRadius: '6px',
                                            background: `${worker.color}10`, color: worker.color,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            <IconComponent size={18} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#334155' }}>{worker.name}</div>
                                            <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{worker.role}</div>
                                        </div>
                                    </div>
                                    {!worker.locked && isSelected && <Check size={16} color={worker.color} />}
                                    {worker.locked && <Lock size={14} color="#cbd5e1" />}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Performance Section (Bottom) */}
                <div style={{ padding: '20px', borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Activity size={14} /> RANK PERFORMANCE
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                        Select a battle to view performance metrics.
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AgentBattleInterface;